import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Subject} from 'rxjs';
import moment from 'moment';
import {takeUntil} from 'rxjs/operators';

import {SchoolYearRolloverModel, SchoolYearRolloverSettingsStatus, SchoolYearRolloverSettingsType} from '@app/core/model/rollover-model';
import {environment} from '@env/environment';
import {copyData} from '@app/core/model/integration-models';
import {OrgsService} from '@app/core/service/orgs.service';

@Component({
    selector: 'ngc-data-rollover-setting',
    templateUrl: './data-rollover-settings.component.html',
    styleUrls: ['./data-rollover-settings.component.scss']
})
export class DataRolloverSettingsComponent implements OnInit, OnDestroy {

    @Input() set settings(value) {
        this.dataRolloverSettings = copyData(value);
    }

    @Input() hasDataRolloverSettingsLoadError = false;
    @Input() isSystemSettings = false;
    @Input() isPreviewDialog = false;
    @Input() onSave$: Subject<void>;
    @Input() orgName: string;
    @Input() formGroup: UntypedFormGroup = this.formBuilder.group({});

    @Output() settingChanged: EventEmitter<any> = new EventEmitter();
    @Output() cancelEdit: EventEmitter<void> = new EventEmitter();
    @Output() saveDataRolloverSettings: EventEmitter<SchoolYearRolloverModel[]> = new EventEmitter();

    dataRolloverSettings: SchoolYearRolloverModel[] = [];
    datePickerFilters = {};
    formReady = false;

    public readonly RolloverSettingsFriendlyNamesMap = new Map<SchoolYearRolloverSettingsType, { name: string; tooltip: string }>([
        [SchoolYearRolloverSettingsType.SuspendDataImport, {
            name: 'Suspend data import',
            tooltip: `This option allows to suspend data importing to ${environment.customer.customerShortName} from all sources.`
        }],
        [SchoolYearRolloverSettingsType.DisableDataDeletion, {
            name: 'Disable data deletion',
            tooltip: 'This option allows to keep data import active, with the support of creating and updating data, but without deleting data.'
        }]
    ]);
    readonly SchoolYearRolloverSettingsType = SchoolYearRolloverSettingsType;

    private unsubscribe$ = new Subject<void>();

    constructor(private formBuilder: UntypedFormBuilder) {
    }

    ngOnInit(): void {
        if (this.dataRolloverSettings) {
            this.dataRolloverSettings.sort((a: SchoolYearRolloverModel, b: SchoolYearRolloverModel) => this.sortRolloverSettings(a, b));

            const nowDate = OrgsService.getNowLocalStartDate();
            this.dataRolloverSettings.forEach(
                (rollOverSetting: SchoolYearRolloverModel) => {
                    const validators = [this.maxDateValidator];
                    const minStartDateValidator = this.getMinDateValidator(
                        true,
                        rollOverSetting.startDateTimestamp
                    );

                    const endDateSetting = new Date(rollOverSetting?.endDateTimestamp);
                    if (endDateSetting < nowDate) {
                        rollOverSetting.isEnabled = false;
                        rollOverSetting.startDateTimestamp = null;
                        rollOverSetting.endDateTimestamp = null;
                    }

                    if (rollOverSetting.isEnabled) {
                        validators.push(Validators.required);
                    }
                    const settingFormGroup = new UntypedFormGroup({
                            isEnabled: new UntypedFormControl(rollOverSetting.isEnabled || this.isSystemSettings || this.isPreviewDialog),
                            startDate: new UntypedFormControl(
                                {
                                    value: (endDateSetting > nowDate) ? moment(rollOverSetting.startDateTimestamp) : null,
                                    disabled: !rollOverSetting.isEnabled && !this.isSystemSettings && !this.isPreviewDialog
                                },
                                [...validators, minStartDateValidator]
                            ),
                            endDate: new UntypedFormControl(
                                {
                                    value: (endDateSetting > nowDate) ? moment(rollOverSetting.endDateTimestamp) : null,
                                    disabled: !rollOverSetting.isEnabled && !this.isSystemSettings && !this.isPreviewDialog
                                },
                                [...validators, this.getMinDateValidator(false)]
                            ),
                            duration: new UntypedFormControl()
                        },
                        [this.dateComparisonValidator]
                    );
                    this.datePickerFilters[rollOverSetting.settingsType] = this.getDatePickerFilter(rollOverSetting);
                    this.formGroup.addControl(rollOverSetting.settingsType, settingFormGroup);
                    settingFormGroup.controls.startDate.updateValueAndValidity();
                    settingFormGroup.controls.endDate.updateValueAndValidity();
                    settingFormGroup.updateValueAndValidity();
                    this.formGroup.updateValueAndValidity();
                }
            );
            this.formReady = true;

            this.formGroup.valueChanges.pipe(takeUntil(this.unsubscribe$))
                .subscribe(value => {
                        this.dataRolloverSettings.forEach((setting: SchoolYearRolloverModel) => {
                            setting.isEnabled = this.formGroup.value[setting.settingsType].isEnabled
                                || this.isSystemSettings
                                || this.isPreviewDialog;

                            const startDate = (this.formGroup.controls[setting.settingsType] as UntypedFormGroup).controls['startDate'].value;
                            const endDate = (this.formGroup.controls[setting.settingsType] as UntypedFormGroup).controls['endDate'].value;
                            const duration = (this.formGroup.controls[setting.settingsType] as UntypedFormGroup).controls['duration']?.value;

                            setting.startDateTimestamp = startDate ? new Date(Date.UTC(startDate.year(), startDate.month(), startDate.date())) : null;
                            setting.endDateTimestamp = endDate ? new Date(Date.UTC(endDate.year(), endDate.month(), endDate.date())) : null;
                            setting.duration = +duration;
                        });

                        this.settingChanged.emit(this.formGroup.valid ? this.dataRolloverSettings : null);
                    }
                );

            if (this.isPreviewDialog) {
                this.onSave$.pipe(takeUntil(this.unsubscribe$))
                    .subscribe(() => {
                        if (this.formGroup.valid) {
                            this.saveDataRolloverSettings.emit(this.dataRolloverSettings);
                        }
                    });
            }
        }
    }

    selectGroup(dataRolloverSettings: SchoolYearRolloverModel, checked: boolean, settingsType?: SchoolYearRolloverSettingsType) {
        settingsType = dataRolloverSettings?.settingsType || settingsType;
        const action = checked ? 'enable' : 'disable';
        const validators = [this.maxDateValidator];
        if (checked) {
            validators.push(Validators.required);
        }
        const minStartDateValidator = this.getMinDateValidator(
            true,
            dataRolloverSettings?.startDateTimestamp ? dataRolloverSettings.startDateTimestamp : null
        );

        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['startDate'][action]();
        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['endDate'][action]();
        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['startDate'].setValidators([...validators, minStartDateValidator]);
        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['endDate'].setValidators([...validators, this.getMinDateValidator(false)]);
        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['startDate'].updateValueAndValidity();
        (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['endDate'].updateValueAndValidity();

        if (this.formGroup.value[settingsType] && this.formGroup.value[settingsType]['startDate']) {
            (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['startDate'].markAsTouched();
        }
        if (this.formGroup.value[settingsType] && this.formGroup.value[settingsType]['endDate']) {
            (this.formGroup.controls[settingsType] as UntypedFormGroup).controls['endDate'].markAsTouched();
        }
    }

    sortRolloverSettings(a: SchoolYearRolloverModel, b: SchoolYearRolloverModel) {
        const settingsOrder = [
            SchoolYearRolloverSettingsType.SuspendDataImport,
            SchoolYearRolloverSettingsType.DisableDataDeletion
        ];

        return settingsOrder.indexOf(a.settingsType) - settingsOrder.indexOf(b.settingsType) || a.settingsType.localeCompare(b.settingsType);
    }

    getDatePickerFilter(rollOverSetting: SchoolYearRolloverModel) {
        const startedDate = rollOverSetting && rollOverSetting.status === SchoolYearRolloverSettingsStatus.BEGAN ?
            new Date(rollOverSetting.startDateTimestamp) : null;

        return (date: Date | null): boolean => {
            const minDate = new Date().setHours(0, 0, 0, 0);
            const maxDate = new Date(2099, 11, 30);

            return date && moment(date).isSameOrAfter(minDate) && moment(date).isSameOrBefore(maxDate) ||
                date && startedDate && moment(date).isSame(startedDate, 'day');
        };
    }

    dateComparisonValidator(formGroup: UntypedFormGroup): { [s: string]: boolean } {
        if (formGroup.controls['startDate'].value
            && formGroup.controls['endDate'].value
            && formGroup.controls['endDate'].value.isSameOrBefore(formGroup.controls['startDate'].value)
        ) {
            formGroup.controls['endDate'].setErrors({'invalid': true});
            return {'endDateIsSameOrBeforeStartDate': true};
        }
        return null;
    }

    getMinDateValidator(isStartDate: boolean, savedDate?: Date) {
        return (formControl: UntypedFormControl): { [s: string]: boolean } => {
            const isPeriodBegan = formControl?.value < new Date();
            const minDate = new Date().setHours(0, 0, 0, 0);
            const isStartDateInBeganPeriod = isStartDate
                && isPeriodBegan
                && formControl?.value
                && formControl.value.isSame(moment(savedDate), 'day');

            if (formControl.value && formControl.value.isBefore(minDate) && !isStartDateInBeganPeriod) {
                return {'minDateError': true};
            }
            return null;
        };
    }

    maxDateValidator(formControl: UntypedFormControl): { [s: string]: boolean } {
        const maxDate = new Date(2099, 11, 30);
        if (formControl.value && formControl.value.isAfter(maxDate)) {
            return {'maxDateError': true};
        }
        return null;
    }

    onSaveDataRolloverSettings() {
        this.saveDataRolloverSettings.emit(this.dataRolloverSettings);
    }

    onCancelEdit() {
        this.cancelEdit.emit();
    }

    changeDateOrDuration(index: number) {
        const settingsType = this.dataRolloverSettings[index].settingsType;
        const oppositeSettingsType = this.getOppositeSettingsType(settingsType);

        (<FormGroup>this.formGroup.controls[oppositeSettingsType])?.controls?.startDate?.updateValueAndValidity();
        (<FormGroup>this.formGroup.controls[oppositeSettingsType])?.controls?.endDate?.updateValueAndValidity();
        (<FormGroup>this.formGroup.controls[oppositeSettingsType])?.controls?.duration?.updateValueAndValidity();
    }

    isFormFieldRequired(settingsType: SchoolYearRolloverSettingsType): boolean {
        const hasValue = this.hasSettingTypeValue(settingsType);
        const oppositeSettingsType = this.getOppositeSettingsType(settingsType);
        const hasOppositeSettingsValue = this.hasSettingTypeValue(oppositeSettingsType);

        return (this.isPreviewDialog || this.isSystemSettings)
            ? (hasValue || (!hasValue && !hasOppositeSettingsValue))
            : this.formGroup.controls[settingsType]?.value.isEnabled;
    }

    private hasSettingTypeValue(settingsType: SchoolYearRolloverSettingsType): boolean {
        return !!(<FormGroup>this.formGroup.controls[settingsType])?.controls?.startDate?.value
            || !!(<FormGroup>this.formGroup.controls[settingsType])?.controls?.endDate?.value
            || !!(<FormGroup>this.formGroup.controls[settingsType])?.controls?.duration?.value;
    }

    private getOppositeSettingsType(settingsType: SchoolYearRolloverSettingsType) {
        return settingsType === this.SchoolYearRolloverSettingsType.SuspendDataImport
            ? SchoolYearRolloverSettingsType.DisableDataDeletion
            : SchoolYearRolloverSettingsType.SuspendDataImport;
    }

    public ngOnDestroy() {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }
}
